import { useEffect, useRef, useState } from "react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Camera, RotateCcw, RefreshCw, X, Loader2, Check } from "lucide-react";
import { toast } from "sonner";

/**
 * Live camera capture using getUserMedia.
 * Props:
 *   open, onOpenChange
 *   onCapture(blob, filename) — called when user confirms the shot; parent handles upload
 *   kindLabel — "Sebelum" / "Sesudah" shown in header
 */
export default function CameraCapture({ open, onOpenChange, onCapture, kindLabel = "Foto" }) {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const [facing, setFacing] = useState("environment"); // 'environment' (rear) | 'user' (front)
  const [error, setError] = useState("");
  const [ready, setReady] = useState(false);
  const [captured, setCaptured] = useState(null); // { blob, dataUrl }
  const [saving, setSaving] = useState(false);

  const stopStream = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
  };

  const startStream = async (mode) => {
    stopStream();
    setError("");
    setReady(false);
    try {
      if (!navigator.mediaDevices?.getUserMedia) {
        throw new Error("Kamera tidak didukung di perangkat/browser ini");
      }
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: { ideal: mode },
          width:  { ideal: 1920 },
          height: { ideal: 1080 },
        },
        audio: false,
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play().catch(() => {});
        setReady(true);
      }
    } catch (e) {
      setError(e.message || String(e));
    }
  };

  useEffect(() => {
    if (open) {
      setCaptured(null);
      startStream(facing);
    } else {
      stopStream();
    }
    return () => stopStream();
    // eslint-disable-next-line
  }, [open]);

  const flipCamera = async () => {
    const next = facing === "environment" ? "user" : "environment";
    setFacing(next);
    await startStream(next);
  };

  const shoot = async () => {
    if (!videoRef.current || !canvasRef.current || !ready) return;
    const video = videoRef.current;
    const canvas = canvasRef.current;
    const w = video.videoWidth || 1280;
    const h = video.videoHeight || 720;
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(video, 0, 0, w, h);
    const dataUrl = canvas.toDataURL("image/jpeg", 0.88);
    canvas.toBlob(
      (blob) => {
        if (!blob) {
          toast.error("Gagal mengambil foto");
          return;
        }
        setCaptured({ blob, dataUrl });
      },
      "image/jpeg",
      0.88
    );
  };

  const retake = () => setCaptured(null);

  const confirm = async () => {
    if (!captured) return;
    setSaving(true);
    const filename = `kamera_${new Date().toISOString().replace(/[:.]/g, "-")}.jpg`;
    try {
      await onCapture?.(captured.blob, filename);
      onOpenChange(false);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-2xl p-0 overflow-hidden border-0 bg-[#0A0A0A] text-white gap-0"
        data-testid="camera-modal"
      >
        <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
          <div className="flex items-center gap-2">
            <Camera className="h-4 w-4 text-[#0055FF]" />
            <div>
              <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-white/50">
                Kamera Langsung
              </div>
              <div className="text-sm font-display font-bold tracking-tight">Foto {kindLabel}</div>
            </div>
          </div>
          <button
            onClick={() => onOpenChange(false)}
            className="h-8 w-8 rounded-full hover:bg-white/10 flex items-center justify-center"
            data-testid="camera-close"
            aria-label="Tutup"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="relative bg-black aspect-video">
          {!captured ? (
            <video
              ref={videoRef}
              autoPlay
              muted
              playsInline
              className="w-full h-full object-cover"
              data-testid="camera-preview"
            />
          ) : (
            <img src={captured.dataUrl} alt="captured" className="w-full h-full object-contain" data-testid="camera-captured-preview" />
          )}

          {!ready && !error && !captured && (
            <div className="absolute inset-0 flex items-center justify-center text-white/60 text-sm">
              <Loader2 className="h-5 w-5 animate-spin mr-2" />
              Menyalakan kamera...
            </div>
          )}

          {error && (
            <div className="absolute inset-0 flex flex-col items-center justify-center px-6 text-center gap-3">
              <Camera className="h-8 w-8 text-white/40" />
              <div className="text-sm text-white/80 max-w-sm" data-testid="camera-error">
                {error}
              </div>
              <div className="text-xs text-white/50 max-w-sm">
                Pastikan akses kamera diberikan atau gunakan tombol Unggah.
              </div>
            </div>
          )}

          <canvas ref={canvasRef} className="hidden" />
        </div>

        <div className="px-4 py-4 flex items-center justify-between gap-3">
          {!captured ? (
            <>
              <button
                onClick={flipCamera}
                disabled={!ready}
                className="h-11 px-4 rounded-full border border-white/20 hover:bg-white/5 disabled:opacity-40 text-xs font-semibold flex items-center gap-2"
                data-testid="camera-flip"
              >
                <RefreshCw className="h-4 w-4" /> {facing === "environment" ? "Depan" : "Belakang"}
              </button>

              <button
                onClick={shoot}
                disabled={!ready}
                data-testid="camera-shoot"
                aria-label="Ambil foto"
                className="h-16 w-16 rounded-full bg-white hover:bg-white/90 active:scale-95 transition-transform disabled:opacity-40 flex items-center justify-center"
              >
                <span className="h-12 w-12 rounded-full border-4 border-[#0A0A0A]" />
              </button>

              <div className="w-24" /> {/* spacer symmetry */}
            </>
          ) : (
            <>
              <button
                onClick={retake}
                data-testid="camera-retake"
                className="h-11 px-4 rounded-full border border-white/20 hover:bg-white/5 text-xs font-semibold flex items-center gap-2"
              >
                <RotateCcw className="h-4 w-4" /> Ulangi
              </button>
              <button
                onClick={confirm}
                disabled={saving}
                data-testid="camera-confirm"
                className="h-11 px-5 rounded-full bg-[#0055FF] hover:bg-[#0044CC] disabled:opacity-60 text-white text-sm font-semibold flex items-center gap-2"
              >
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
                Simpan Foto
              </button>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
